import classNames from 'classnames';
import { useMemo } from 'react';

import { getProgressBreakdown } from 'my-core/online-course-user-utils';

import Label from 'my-elements/Label';

import Typography from '@mui/material/Typography';

const rootCss = ({ palette, spacing }) => ({
  '.OCPB-legend': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: spacing(1),
  },
  '.OCPB-title': { marginRight: spacing(1) },
  '.OCPB-labels': {
    display: 'flex',
    justifyContent: 'space-between',
  },
  '.OCPB-label': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: spacing(1, 1, 1, 0),
  },
  '.OCPB-label_mini': { padding: spacing(0.5, 1, 0.5, 0) },
  '.OCPB-labelNumber': {
    marginRight: spacing(1),
  },
  '.OCPB-labelText': {
    color: palette.text.secondary,
  },
  '&.OCPB-root_bgdark .OCPB-labelText': { color: palette.common.white },

  '.OCPB-barsWrapper': {
    display: 'flex',
    height: 16,
    borderRadius: 8,
    padding: 4,
    backgroundColor: palette.grey[50],
    boxShadow: 'inset 0px 0px 4px rgba(0, 0, 0, 0.09)',
  },
  '&.OCPB-root_bgdark .OCPB-barsWrapper': {
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  '&.OCPB-root_large .OCPB-barsWrapper': {
    height: 24,
    borderRadius: 12,
    padding: 6,
  },

  '.OCPB-bar': {
    height: '100%',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 4,
      right: 4,
      height: '100%',
      borderRadius: 4,
    },
  },
  '&.OCPB-root_large .OCPB-bar:before': { borderRadius: 8 },
  '.OCPB-bar_green:before': { backgroundColor: palette.green.main },
  '.OCPB-bar_yellow:before': { backgroundColor: palette.yellow.main },
  '.OCPB-bar_grey:before': { backgroundColor: palette.grey[200] },
});

export default function OnlineCourseProgressBreakdown(props) {
  const { bgDark, breakdown: breakdownProp, className, large, mini, onlineCourse, onlineCourseUser } = props;

  const breakdown = useMemo(
    () => breakdownProp || getProgressBreakdown(onlineCourseUser, onlineCourse),
    [breakdownProp, onlineCourse, onlineCourseUser],
  );

  return (
    <div className={classNames(className, { 'OCPB-root_large': large, 'OCPB-root_bgdark': bgDark })} css={rootCss}>
      <div className="OCPB-legend">
        {!mini && (
          <Typography className="OCPB-title" variant="subtitle2">
            Activities
          </Typography>
        )}
        <div className="OCPB-labels">
          {renderLabel(breakdown.completed, 'Completed', 'green')}
          {renderLabel(breakdown.in_progress, 'In Progress', 'yellow')}
          {renderLabel(breakdown.not_started, 'Not Started', 'grey')}
        </div>
      </div>
      <div className="OCPB-barsWrapper">
        <div
          className="OCPB-bar OCPB-bar_green"
          style={{ width: `${(breakdown.completed / breakdown.total) * 100}%` }}
        />
        <div
          className="OCPB-bar OCPB-bar_yellow"
          style={{ width: `${(breakdown.in_progress / breakdown.total) * 100}%` }}
        />
        <div
          className="OCPB-bar OCPB-bar_grey"
          style={{ width: `${(breakdown.not_started / breakdown.total) * 100}%` }}
        />
      </div>
    </div>
  );

  function renderLabel(number, label, color) {
    return (
      <div className={classNames('OCPB-label', { 'OCPB-label_mini': mini })}>
        <Label className="OCPB-labelNumber" color={color} label={number} size={mini ? 'small' : undefined} />
        <Typography className="OCPB-labelText" noWrap variant="caption">
          {label}
        </Typography>
      </div>
    );
  }
}
